import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createClient } from "@supabase/supabase-js";
import { ConsoleApiError, createConsoleApi } from "@/api/consoleClient";
import { createDemoApi } from "@/demo/demoApi";
import { DEMO_CLIENT, DEMO_MEMBER, DEMO_MEMBERSHIPS } from "@/demo/demoData";
import { isDemoViewEnabled, persistDemoSession, readStoredDemoSession } from "@/demo/demoConfig";
import { startProviderSignInOnly } from "@/auth/providerOAuthPolicy";
import { READ_ONLY_MESSAGE, VIEW_AS_ERROR_CODES, persistViewAs, readOnlyApi, readStoredViewAs } from "@/components/viewAs/viewAsModel";

const ACTIVE_CLIENT_KEY = "growthos.active-client";
// Exported for the development fixture under /__dev only; the app always goes through AppProvider.
export const AppContext = createContext(null);

export const useApp = () => {
  const value = useContext(AppContext);
  if (!value) throw new Error("useApp must be used inside AppProvider");
  return value;
};

function storedClientSlug() {
  try {
    return window.localStorage.getItem(ACTIVE_CLIENT_KEY);
  } catch {
    return null;
  }
}

function persistClientSlug(slug) {
  try {
    if (slug) window.localStorage.setItem(ACTIVE_CLIENT_KEY, slug);
    else window.localStorage.removeItem(ACTIVE_CLIENT_KEY);
  } catch {
    // Browser storage can be blocked. The in-memory active tenant remains safe
    // for this tab; the user will select it again next visit.
  }
}

export function AppProvider({ children }) {
  const demoAvailable = isDemoViewEnabled();
  const [demoView, setDemoView] = useState(() => demoAvailable && readStoredDemoSession());
  const [authStatus, setAuthStatus] = useState("loading");
  const [authError, setAuthError] = useState(null);
  // The account behind the browser's session, known even when no workspace accepts it.
  const [sessionEmail, setSessionEmail] = useState(null);
  const [member, setMember] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [activeClient, setActiveClient] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [authClientVersion, setAuthClientVersion] = useState(0);
  const [authProviders, setAuthProviders] = useState([]);
  // View as (staff only): who the dashboard reads as, as the server confirmed it, and whether this login may start one.
  const [viewingAs, setViewingAs] = useState(null);
  const [canViewAs, setCanViewAs] = useState(false);

  const mountedRef = useRef(true);
  const supabaseRef = useRef(null);
  const sessionRef = useRef(null);
  const demoViewRef = useRef(demoView);
  const demoApiRef = useRef(null);
  const activeClientSlugRef = useRef(storedClientSlug());
  const viewAsRef = useRef(readStoredViewAs());
  const bootstrapGenerationRef = useRef(0);
  const apiRef = useRef(
    createConsoleApi({
      getAccessToken: async () => sessionRef.current?.access_token ?? null,
      getActiveClientSlug: () => activeClientSlugRef.current,
      getViewAsTarget: () => viewAsRef.current?.email ?? null,
    })
  );
  // The same api with every action refused, for as long as a look lasts.
  const readOnlyApiRef = useRef(null);
  if (!readOnlyApiRef.current) readOnlyApiRef.current = readOnlyApi(apiRef.current);

  const clearIdentity = useCallback((nextStatus = "unauthenticated") => {
    bootstrapGenerationRef.current += 1;
    sessionRef.current = null;
    activeClientSlugRef.current = null;
    persistClientSlug(null);
    viewAsRef.current = null;
    persistViewAs(null);
    if (!mountedRef.current) return;
    setMember(null);
    setMemberships([]);
    setActiveClient(null);
    setViewingAs(null);
    setCanViewAs(false);
    setSessionEmail(null);
    setAuthError(null);
    setAuthStatus(nextStatus);
  }, []);

  const applyDemoIdentity = useCallback(() => {
    bootstrapGenerationRef.current += 1;
    if (!demoApiRef.current) demoApiRef.current = createDemoApi();
    sessionRef.current = null;
    activeClientSlugRef.current = DEMO_CLIENT.clientSlug;
    viewAsRef.current = null;
    persistViewAs(null);
    if (!mountedRef.current) return;
    setViewingAs(null);
    setCanViewAs(false);
    setMember({ ...DEMO_MEMBER });
    setMemberships(DEMO_MEMBERSHIPS.map((membership) => ({ ...membership })));
    setActiveClient({ ...DEMO_CLIENT });
    setAuthError(null);
    setAuthStatus("authenticated");
  }, []);

  const bootstrapMember = useCallback(async ({ retryStaleClient = true, recoverViewAs = true } = {}) => {
    const bootstrapGeneration = ++bootstrapGenerationRef.current;
    if (mountedRef.current) setSessionEmail(sessionRef.current?.user?.email ?? null);
    // A look the server will not continue (the person left the brand, say) ends here and the viewer's own dashboard
    // loads instead. Starting a look passes recoverViewAs: false, so the picker hears why it did not start.
    const readMe = async (options) => {
      try {
        return await apiRef.current.getMe(options);
      } catch (error) {
        if (!recoverViewAs || !viewAsRef.current || !(error instanceof ConsoleApiError) || !VIEW_AS_ERROR_CODES.has(error.code)) throw error;
        viewAsRef.current = null;
        persistViewAs(null);
        return apiRef.current.getMe(options);
      }
    };
    try {
      let payload;
      try {
        payload = await readMe();
      } catch (error) {
        if (!mountedRef.current || bootstrapGeneration !== bootstrapGenerationRef.current) throw error;
        if (
          retryStaleClient &&
          error instanceof ConsoleApiError &&
          ["client_choice_required", "client_forbidden"].includes(error.code) &&
          activeClientSlugRef.current
        ) {
          activeClientSlugRef.current = null;
          persistClientSlug(null);
          payload = await readMe({ omitActiveClient: true });
        } else {
          throw error;
        }
      }

      if (!mountedRef.current || bootstrapGeneration !== bootstrapGenerationRef.current) return payload;
      setMember(payload.member);
      setMemberships(payload.memberships ?? []);
      setActiveClient(payload.client ?? null);
      setViewingAs(payload.viewingAs ?? null);
      setCanViewAs(payload.canViewAs === true);
      setAuthError(null);

      if (payload.client) {
        activeClientSlugRef.current = payload.client.clientSlug;
        persistClientSlug(payload.client.clientSlug);
        setAuthStatus("authenticated");
      } else {
        activeClientSlugRef.current = null;
        persistClientSlug(null);
        setAuthStatus("select-client");
      }
      return payload;
    } catch (error) {
      if (!mountedRef.current || bootstrapGeneration !== bootstrapGenerationRef.current) return null;
      // A look that did not start leaves the dashboard exactly as it was; the caller says why.
      if (!recoverViewAs && error instanceof ConsoleApiError && VIEW_AS_ERROR_CODES.has(error.code)) throw error;
      if (error instanceof ConsoleApiError && error.status === 401) {
        clearIdentity("unauthenticated");
      } else if (error instanceof ConsoleApiError && error.code === "no_membership") {
        setAuthError(error);
        setAuthStatus("access-denied");
      } else {
        setAuthError(error);
        setAuthStatus("error");
      }
      throw error;
    }
  }, [clearIdentity]);

  const initializeAuth = useCallback(async () => {
    if (!mountedRef.current) return;
    if (demoViewRef.current) return;
    setAuthStatus("loading");
    setAuthError(null);
    try {
      const config = await apiRef.current.getPublicConfig();
      if (!config?.authUrl || !config?.authAnonKey) {
        throw new ConsoleApiError("Authentication configuration is unavailable.", {
          code: "auth_config_missing",
        });
      }
      setAuthProviders(Array.isArray(config.authProviders) ? config.authProviders : []);

      let supabase = supabaseRef.current;
      if (!supabase) {
        supabase = createClient(config.authUrl, config.authAnonKey, {
          auth: {
            persistSession: true,
            autoRefreshToken: true,
            detectSessionInUrl: true,
            flowType: "pkce",
          },
        });
        supabaseRef.current = supabase;
        setAuthClientVersion((version) => version + 1);
      }

      const { data, error } = await supabase.auth.getSession();
      if (error) throw error;
      sessionRef.current = data.session;
      if (!data.session) {
        clearIdentity("unauthenticated");
        return;
      }
      await bootstrapMember();
    } catch (error) {
      if (!mountedRef.current) return;
      if (error instanceof ConsoleApiError && (error.status === 401 || error.code === "no_membership")) {
        return;
      }
      setAuthError(error);
      setAuthStatus("error");
    }
  }, [bootstrapMember, clearIdentity]);

  useEffect(() => {
    mountedRef.current = true;
    if (demoViewRef.current) applyDemoIdentity();
    else initializeAuth();
    return () => {
      mountedRef.current = false;
    };
  }, [applyDemoIdentity, initializeAuth]);

  const enterDemoView = useCallback(() => {
    if (!demoAvailable) return;
    demoViewRef.current = true;
    persistDemoSession(true);
    setDemoView(true);
    applyDemoIdentity();
  }, [applyDemoIdentity, demoAvailable]);

  const exitDemoView = useCallback(() => {
    demoViewRef.current = false;
    persistDemoSession(false);
    setDemoView(false);
    clearIdentity("unauthenticated");
    initializeAuth();
  }, [clearIdentity, initializeAuth]);

  useEffect(() => {
    const supabase = supabaseRef.current;
    if (!supabase) return undefined;
    const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      sessionRef.current = nextSession;
      window.setTimeout(() => {
        if (!mountedRef.current) return;
        if (!nextSession) clearIdentity("unauthenticated");
        else bootstrapMember().catch(() => undefined);
      }, 0);
    });
    return () => data.subscription.unsubscribe();
  }, [authClientVersion, bootstrapMember, clearIdentity]);

  const signInWithProvider = useCallback(async (provider) => {
    const supabase = supabaseRef.current;
    if (!supabase) throw new Error("Authentication is still loading.");
    if (!authProviders.includes(provider)) throw new Error("This sign-in method is not available.");
    setAuthStatus("signing-in");
    setAuthError(null);
    try {
      await startProviderSignInOnly(supabase, window.location.origin, provider);
    } catch (error) {
      setAuthStatus(error?.code === "identity_linking_forbidden" ? "authenticated" : "unauthenticated");
      setAuthError(error);
      throw error;
    }
  }, [authProviders]);

  const signInWithGoogle = useCallback(() => signInWithProvider("google"), [signInWithProvider]);
  const signInWithMicrosoft = useCallback(() => signInWithProvider("azure"), [signInWithProvider]);

  const signOut = useCallback(async () => {
    if (demoViewRef.current) {
      exitDemoView();
      return;
    }
    const supabase = supabaseRef.current;
    try {
      if (supabase) {
        const { error } = await supabase.auth.signOut();
        if (error) await supabase.auth.signOut({ scope: "local" });
      }
    } finally {
      clearIdentity("unauthenticated");
    }
  }, [clearIdentity, exitDemoView]);

  /** Forget the active workspace and show the picker again (the sidebar switcher is desktop-only). */
  const chooseWorkspace = useCallback(() => {
    if (memberships.length < 2) return;
    activeClientSlugRef.current = null;
    persistClientSlug(null);
    setActiveClient(null);
    setAuthStatus("select-client");
  }, [memberships]);

  const selectClient = useCallback(async (clientSlug) => {
    const allowed = memberships.some((membership) => membership.clientSlug === clientSlug);
    if (!allowed) throw new Error("client_choice_required");
    activeClientSlugRef.current = clientSlug;
    persistClientSlug(clientSlug);
    setAuthStatus("loading");
    try {
      await bootstrapMember({ retryStaleClient: false });
    } catch (error) {
      activeClientSlugRef.current = null;
      persistClientSlug(null);
      throw error;
    }
  }, [bootstrapMember, memberships]);

  /** Look at the dashboard as one of the active brand's people. Staff only; the server decides, and refuses writes. */
  const startViewAs = useCallback(async (email) => {
    if (demoViewRef.current) throw new Error("view_as_unavailable");
    const normalized = String(email ?? "").trim().toLowerCase();
    if (!normalized) throw new Error("view_as_target_required");
    const previous = viewAsRef.current;
    viewAsRef.current = { email: normalized };
    persistViewAs(viewAsRef.current);
    try {
      await bootstrapMember({ retryStaleClient: false, recoverViewAs: false });
    } catch (error) {
      viewAsRef.current = previous;
      persistViewAs(previous);
      throw error;
    }
  }, [bootstrapMember]);

  /** Back to the viewer's own dashboard. */
  const stopViewAs = useCallback(async () => {
    if (!viewAsRef.current) return;
    viewAsRef.current = null;
    persistViewAs(null);
    await bootstrapMember({ retryStaleClient: false }).catch(() => undefined);
  }, [bootstrapMember]);

  const markRead = useCallback((id) => {
    setNotifications((previous) => previous.map((item) => (item.id === id ? { ...item, read: true } : item)));
  }, []);
  const markAllRead = useCallback(() => {
    setNotifications((previous) => previous.map((item) => ({ ...item, read: true })));
  }, []);

  const currentApi = useCallback(() => (
    demoViewRef.current && demoApiRef.current ? demoApiRef.current : viewAsRef.current ? readOnlyApiRef.current : apiRef.current
  ), []);

  const addRequest = useCallback(async (request) => {
    return currentApi().createRequest({
      title: request.title,
      body: request.description ?? request.body ?? "",
      context: request.context ?? [request.type, request.area, request.priority].filter(Boolean).join(":"),
    });
  }, [currentApi]);

  const requestUpdate = useCallback(async (skill) => {
    const skillName = String(skill?.name ?? skill ?? "requested-update").slice(0, 130);
    return currentApi().createRequest({
      title: `Review skill update: ${skillName}`,
      body: "Please review and apply the approved skill update for this client workspace.",
      context: `skill:${skillName}`,
    });
  }, [currentApi]);

  const value = useMemo(() => ({
    authStatus,
    authError,
    authed: authStatus === "authenticated",
    sessionEmail,
    member,
    memberships,
    activeClient,
    selectClient,
    chooseWorkspace,
    retryAuth: initializeAuth,
    authProviders,
    signInWithGoogle,
    signInWithMicrosoft,
    signOut,
    api: demoView && demoApiRef.current ? demoApiRef.current : viewingAs ? readOnlyApiRef.current : apiRef.current,
    // View as: set while staff look at the dashboard as someone else; then every action is off and says so.
    viewingAs,
    canViewAs,
    readOnly: Boolean(viewingAs),
    readOnlyMessage: READ_ONLY_MESSAGE,
    startViewAs,
    stopViewAs,
    demoAvailable,
    demoView,
    enterDemoView,
    exitDemoView,
    notifications,
    markRead,
    markAllRead,
    addRequest,
    requestUpdate,
  }), [
    activeClient,
    addRequest,
    authError,
    authStatus,
    authProviders,
    demoAvailable,
    demoView,
    enterDemoView,
    exitDemoView,
    initializeAuth,
    markAllRead,
    markRead,
    member,
    memberships,
    notifications,
    requestUpdate,
    selectClient,
    chooseWorkspace,
    sessionEmail,
    signInWithGoogle,
    signInWithMicrosoft,
    signOut,
    startViewAs,
    stopViewAs,
    viewingAs,
    canViewAs,
  ]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}